import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Line,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import { Card } from '@/components/ui/card/Card';
import type { BoxPlotData } from '../../utils/boxPlotUtils';
import { COLORS } from '../../utils/palette';

export type DistributionMetric = 'duration' | 'pieces' | 'volume' | 'malround';

type DistributionChartProps = {
  /** Selects the title / axis translations, the value formatter and the series colour. */
  metric: DistributionMetric;
  data: BoxPlotData[];
};

interface MetricDisplay {
  format: (value: number) => string;
  color: string;
}

/** Seconds → s / min / h */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  } else if (seconds < 3600) {
    return `${(seconds / 60).toFixed(1)} min`;
  } else {
    return `${(seconds / 3600).toFixed(1)} h`;
  }
}

function formatPieceCount(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toFixed(0);
}

/** cm³, or L from 1000 cm³ */
function formatVolume(volume: number): string {
  if (volume >= 1000) {
    return `${(volume / 1000).toFixed(1)} L`;
  }
  return `${volume.toFixed(1)} cm³`;
}

function formatMalround(malround: number): string {
  return `${malround.toFixed(2)} µm`;
}

const METRICS: Record<DistributionMetric, MetricDisplay> = {
  duration: { format: formatDuration, color: COLORS[0] },
  pieces: { format: formatPieceCount, color: COLORS[1] },
  volume: { format: formatVolume, color: COLORS[2] },
  malround: { format: formatMalround, color: COLORS[3] },
};

/**
 * Box-plot style distribution of one metric per reference (stacked transparent bars for the
 * whiskers, a filled Q1–Q3 box and a median line). One component for the four metrics of the
 * comparison view: they only differ by their labels, value formatting and colour.
 */
export function DistributionChart({ metric, data }: DistributionChartProps) {
  const { t } = useTranslation();
  const { format, color } = METRICS[metric];

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Card padding="sm">
      <h3 className="mb-4 text-sm font-medium text-foreground">
        {t(`analytics.comparison.distribution.${metric}.title`)}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="reference"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => (value.length > 15 ? `${value.substring(0, 12)}...` : value)}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => format(value)}
            label={{
              value: t(`analytics.comparison.distribution.${metric}.yAxisLabel`),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
            }}
          />
          <Tooltip
            formatter={(value) => [format(value as number), '']}
            labelFormatter={(label) => `${t('common.reference')}: ${label}`}
            contentStyle={{
              backgroundColor: 'var(--popover)',
              color: 'var(--popover-foreground)',
              border: '1px solid var(--border)',
              borderRadius: '4px',
            }}
          />

          {/* Whiskers: min to max */}
          <Bar dataKey="min" stackId="box" fill="transparent">
            {data.map((_, index) => (
              <Cell key={`min-${index}`} />
            ))}
          </Bar>
          <Bar dataKey="q1" stackId="box" fill="transparent">
            {data.map((_, index) => (
              <Cell key={`q1-${index}`} />
            ))}
          </Bar>

          {/* Box: Q1 to Q3 */}
          <Bar dataKey="q3" stackId="box" fill={color} fillOpacity={0.6}>
            {data.map((_, index) => (
              <Cell key={`q3-${index}`} />
            ))}
          </Bar>

          {/* Median line */}
          <Line
            type="monotone"
            dataKey="median"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4, fill: color }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
}
